import { FilterBuilder } from "../Common/FilterBuilder";

import { TreePicklistItemResponse, TreeQuery } from "./TreePicklistItemProvider";

export const NESTING_LEVEL_FIELD = "level";
export const DEFAULT_ELEMENT_ID_COLUMN_NAME = "code";
export const DEFAULT_PARENT_ELEMENT_ID_COLUMN_NAME = "childof";

export type TreePicklistElementId = string;
export interface TreePicklistItemParams {
    item: TreePicklistItemResponse;
    ancestors: TreePicklistItemResponse[];
    parentColumnName: string;
    childColumnName: string;
    minPickLevel: number;
    allowedToPickLevels: number[] | undefined;
    fieldToDisableElementPick: string | undefined;
    treeQuery: TreeQuery;
}

export class TreePicklistItem {
    public readonly elementId: TreePicklistElementId;
    public readonly parentElementId: string;
    public readonly childrenCount: number;
    public readonly isAvailableForSaving: boolean;
    public readonly isFinalNode: boolean;

    private readonly item: TreePicklistItemResponse;
    private readonly ancestorsIds: TreePicklistElementId[];
    private readonly parentColumnName: string;
    private readonly childColumnName: string;
    private readonly treeQuery: TreeQuery;
    private readonly nestingLevel?: number;
    private readonly minPickLevel: number;
    private readonly allowedToPickLevels?: number[];
    private readonly fieldToDisableElementPick?: string;

    public constructor(params: TreePicklistItemParams) {
        this.item = params.item;
        this.ancestorsIds = params.ancestors.map(item =>
            this.selectElementIdFromItemResponse(item, params.parentColumnName)
        );
        this.parentColumnName = params.parentColumnName;
        this.childColumnName = params.childColumnName;
        this.childrenCount = Number(params.item.childrenCount);
        this.treeQuery = params.treeQuery;

        const rawNestingLevel = params.item[NESTING_LEVEL_FIELD];
        const hasNestingLevel = Boolean(rawNestingLevel);
        this.nestingLevel = hasNestingLevel ? Number(rawNestingLevel) : undefined;

        this.minPickLevel = params.minPickLevel;
        this.allowedToPickLevels = params.allowedToPickLevels;
        this.fieldToDisableElementPick = params.fieldToDisableElementPick;

        this.isAvailableForSaving = this.checkIsPickAllowed();
        this.isFinalNode = !(this.childrenCount > 0);

        this.elementId = this.selectElementIdFromItemResponse(this.item, params.parentColumnName);

        this.parentElementId = String(
            params.item[params.childColumnName] || params.item[DEFAULT_PARENT_ELEMENT_ID_COLUMN_NAME]
        );
    }

    /**
     * Отрицательное число в кач-ве возвращенного значения означает,
     * что текущий элемент является группировочным (определен через поле "fieldToDisableElementPick")
     * и находится за пределами уровня отсечки "minPickLevel"
     */
    private countLevelsToNearestAvailableNode(): number {
        const nearestOfAllowedToPickLevels = this.countNearestFromAllowedToPickLevels();
        const nearestAvailableLevel = Math.max(this.minPickLevel, nearestOfAllowedToPickLevels);
        return this.nestingLevel ? nearestAvailableLevel - this.nestingLevel : 0;
    }

    private countNearestFromAllowedToPickLevels(): number {
        if (this.allowedToPickLevels && this.allowedToPickLevels.length) {
            const sortedAllowedToPickLevels = this.allowedToPickLevels.toSorted();
            const nearest = sortedAllowedToPickLevels.find(l => l >= this.minPickLevel);
            return nearest ?? 0;
        }

        return 0;
    }

    private selectElementIdFromItemResponse(
        item: TreePicklistItemResponse,
        parentColumnName: string
    ): TreePicklistElementId {
        // у конечного элемента кастомное поле parentColumnName может отсутствовать, но должно присутствовать "code"
        return String(item[parentColumnName] || this.item[DEFAULT_ELEMENT_ID_COLUMN_NAME]);
    }

    public toOriginal(): TreePicklistItemResponse {
        return this.item;
    }

    public hasAncestorWithId(elementId: TreePicklistElementId): boolean {
        return this.ancestorsIds.includes(elementId);
    }

    public async fetchNearestNodesAvailableForSaving(): Promise<TreePicklistItemResponse[]> {
        if (this.isAvailableForSaving) {
            return [this.toOriginal()];
        }

        const levelsToNearestAvailableNode = this.countLevelsToNearestAvailableNode();
        if (levelsToNearestAvailableNode <= 0) {
            return this.fetchNodesFromRelativeLevel(1);
        }

        return this.fetchNodesFromRelativeLevel(levelsToNearestAvailableNode);
    }

    private async fetchNodesFromRelativeLevel(depth: number): Promise<TreePicklistItemResponse[]> {
        const primaryNodes = await this.fetchRelativeNodes([this.toOriginal()], depth);

        const onlyAvailableForSavingNodes = primaryNodes.map(async node => {
            const item = new TreePicklistItem({
                item: node,
                // не корректное значение, не играет роли для дальнейшего использования
                ancestors: [],
                parentColumnName: this.parentColumnName,
                childColumnName: this.childColumnName,
                minPickLevel: this.minPickLevel,
                allowedToPickLevels: this.allowedToPickLevels,
                fieldToDisableElementPick: this.fieldToDisableElementPick,
                treeQuery: this.treeQuery,
            });

            return await item.fetchNearestNodesAvailableForSaving();
        });

        return (await Promise.all(onlyAvailableForSavingNodes)).flat();
    }

    private async fetchRelativeNodes(
        currentNodes: TreePicklistItemResponse[],
        depth: number
    ): Promise<TreePicklistItemResponse[]> {
        if (depth <= 0) {
            return currentNodes;
        }

        const nodes = await this.fetchCombinedChildrenNodes(currentNodes);

        return this.fetchRelativeNodes(nodes, depth - 1);
    }

    private async fetchCombinedChildrenNodes(parents: TreePicklistItemResponse[]): Promise<TreePicklistItemResponse[]> {
        const nodes: TreePicklistItemResponse[] = [];
        for (const node of parents) {
            const children = await this.fetchChildrenNodes(node);
            nodes.push(...children);
        }

        return nodes;
    }

    private async fetchChildrenNodes(parent: TreePicklistItemResponse): Promise<TreePicklistItemResponse[]> {
        const elementId = parent[this.parentColumnName] as string;
        const filterBuilder = new FilterBuilder().column(this.childColumnName).eql(elementId);

        const response = (await this.treeQuery({ nodeFilter: filterBuilder })).items;
        const children = response.filter(item => item[this.childColumnName] === elementId);

        return children;
    }

    private checkIsPickAllowed(): boolean {
        const isLevelInAllowedGroup = this.checkIsLevelInAllowedGroup();
        const isLevelInAllowedScope = this.checkIsLevelInAllowedScope();
        const isAllowedByDisablingField = this.checkIsAllowedByDisablingField();

        const isPickAllowed = isLevelInAllowedGroup && isLevelInAllowedScope && isAllowedByDisablingField;

        return isPickAllowed;
    }

    private checkIsLevelInAllowedGroup(): boolean {
        return this.allowedToPickLevels && this.nestingLevel
            ? Boolean(this.allowedToPickLevels.includes(this.nestingLevel))
            : true;
    }

    private checkIsLevelInAllowedScope(): boolean {
        return this.nestingLevel ? this.nestingLevel >= this.minPickLevel : true;
    }

    private checkIsAllowedByDisablingField(): boolean {
        return this.fieldToDisableElementPick
            ? !this.parseDisablingFieldValue(this.item[this.fieldToDisableElementPick])
            : true;
    }

    private parseDisablingFieldValue(value?: string | number): boolean {
        if (typeof value === "string") {
            return value.toLowerCase() === "true";
        }

        return Boolean(value);
    }
}
